import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

// Two stacked photos fill the left column. Both bind to gallery shots so they
// stay coherent with the property, and editors can swap either independently.
const imageTopDef: SlotGroupDefinition = {
  label: 'Top Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[4].alt_text',
      defaultValue: 'Neighborhood scene',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const imageBottomDef: SlotGroupDefinition = {
  label: 'Bottom Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[5].alt_text',
      defaultValue: 'Neighborhood scene',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * Neighborhood page intro: a two-column stripe below the hero/wave band. The
 * left column stacks two gallery photos; the right column carries the headline
 * and a location blurb. Mirrors the reference site's neighborhood intro.
 */
export function NeighborhoodIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const imageTop = useSlotGroup('neighborhoodintro.imagetop', imageTopDef);
  const imageBottom = useSlotGroup('neighborhoodintro.imagebottom', imageBottomDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-primary)] py-12 md:py-20"
    >
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 lg:gap-24 items-center">
          {/* Left: stacked, staggered photos */}
          <div className="nbi-images">
            <EditableSlotGroup groupPath="neighborhoodintro.imagetop" definition={imageTopDef}>
              <div className="nbi-image-wrap nbi-image-wrap--square">
                {imageTop.image ? (
                  <VibesitesImage
                    url={imageTop.image}
                    alt={imageTop.alt || 'Neighborhood scene'}
                    className="nbi-image"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                ) : (
                  <div className="nbi-image nbi-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="neighborhoodintro.imagebottom" definition={imageBottomDef}>
              <div className="nbi-image-wrap nbi-image-wrap--landscape">
                {imageBottom.image ? (
                  <VibesitesImage
                    url={imageBottom.image}
                    alt={imageBottom.alt || 'Neighborhood scene'}
                    className="nbi-image"
                    sizes="(max-width: 768px) 100vw, 36vw"
                  />
                ) : (
                  <div className="nbi-image nbi-image--placeholder" />
                )}
              </div>
            </EditableSlotGroup>
          </div>

          {/* Right: headline + blurb */}
          <div>
            <RichTextContent
              path="neighborhoodintro.heading"
              label="Heading"
              defaultValue='<p>Explore the <span data-type="field-token" data-field="property_name"></span> Neighborhood</p>'
              as="h2"
              className="text-[clamp(1.75rem,4.4vw,3rem)] uppercase tracking-wide text-[var(--vibe-color-text-on-primary)] leading-[1.05] mb-6"
              style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
            />

            <RichTextContent
              path="neighborhoodintro.body"
              label="Body Text"
              defaultField="property_neighborhood_blurb"
              defaultValue='<p>Located in the vibrant Hyde Park neighborhood, <span data-type="field-token" data-field="property_name"></span> offers easy access to dining, shopping, and entertainment. Enjoy nearby attractions like Anderson Park and Hyde Park Village, all just minutes from home. With everything you need close by, <span data-type="field-token" data-field="property_name"></span> provides one of the best apartment living experiences in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>'
              as="div"
              className="font-body text-base leading-relaxed text-[var(--vibe-color-text-on-primary)] [&_p]:mb-4 [&_a]:text-[var(--vibe-color-text-on-primary)] [&_a]:underline"
            />
          </div>
        </div>
      </div>

      <style>{`
        .nbi-images {
          position: relative;
        }
        .nbi-image-wrap {
          width: 100%;
          overflow: hidden;
          border-radius: 0;
        }
        .nbi-image {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }

        /* Top image: larger, anchors the collage. Pulled in from the right
           edge so the smaller image can stick out past it. */
        .nbi-image-wrap--square {
          aspect-ratio: 4 / 3;
          width: calc(100% - 48px);
        }

        /* Bottom image: smaller, offset to the lower-right so it overlaps the
           larger image and its lower portion drops below. The overlap uses a %
           margin (relative to column width) so the ratio holds at any width. */
        .nbi-image-wrap--landscape {
          aspect-ratio: 3 / 2;
          width: 58%;
          margin-left: auto;
          margin-top: -30%;
          position: relative;
          z-index: 2;
          box-shadow: 0 18px 42px rgba(0, 0, 0, 0.16);
        }

        /* Desktop: the top image is a touch shorter and pulled in further; the
           bottom image is a bit larger and overlaps ~3/4 of its own height so
           only its lower quarter drops below (matches the reference). */
        @media (min-width: 768px) {
          .nbi-image-wrap--square {
            aspect-ratio: 11 / 10;
            width: calc(100% - 60px);
          }
          .nbi-image-wrap--landscape {
            width: 72%;
            margin-top: -36%;
          }
        }

        .nbi-image--placeholder {
          background: var(--vibe-color-surface);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodIntro',
  displayName: 'Neighborhood Intro',
  tokens: {
    'neighborhoodintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Explore the <span data-type="field-token" data-field="property_name"></span> Neighborhood</p>',
    },
    'neighborhoodintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_neighborhood_blurb',
      defaultValue:
        '<p>Located in the vibrant Hyde Park neighborhood, <span data-type="field-token" data-field="property_name"></span> offers easy access to dining, shopping, and entertainment. Enjoy nearby attractions like Anderson Park and Hyde Park Village, all just minutes from home. With everything you need close by, <span data-type="field-token" data-field="property_name"></span> provides one of the best apartment living experiences in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>',
    },
  },
  slotGroups: {
    'neighborhoodintro.imagetop': imageTopDef,
    'neighborhoodintro.imagebottom': imageBottomDef,
  },
};

(NeighborhoodIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodIntro;
